import { ApolloLink } from 'apollo-link';
import { HttpLink } from 'apollo-link-http';
import { RetryLink } from 'apollo-link-retry';
import { onError } from 'apollo-link-error';
import stateLink from './stateLink';
import { AUTH_TOKEN, GUEST_TOKEN, LOCALE } from 'config/constants';
import { toast } from 'react-toastify';

// ---------------------------------------------------------------------------
// LOCAL BACKEND SWITCH — flip this by hand. No env vars, nothing to configure.
//
//   false (default) -> production API, https://api.lynks.com/graphql
//   true            -> local Rails backend, http://localhost:3030/graphql
//
// It is a DEVELOPMENT-ONLY switch, and structurally so: the branch that reads
// it (below) is guarded on `process.env.NODE_ENV !== 'production'`, which
// webpack's DefinePlugin substitutes at build time, so a production build folds
// the whole block away and drops `LOCAL_GRAPHQL_URI` with it. Whatever this
// constant is left set to, a production bundle contains no localhost URL and
// every visitor gets api.lynks.com. Nothing here has to be remembered before a
// commit or a Vercel deploy.
//
// The normal frontend-only workflow (`yarn start`) runs against production and
// needs no local backend, so `false` is still the sensible resting state. Set
// it to `true` while you actually have the `dealgamed` Rails app running on
// port 3030 — e.g. to use a GraphQL field that exists locally but is not
// deployed yet.
//
// To hit a backend on another machine (phone testing over LAN, etc.) just edit
// LOCAL_GRAPHQL_URI, e.g. 'http://192.168.0.10:3030/graphql'.
// ---------------------------------------------------------------------------
const USE_LOCAL_BACKEND = false;
const LOCAL_GRAPHQL_URI = 'http://localhost:3030/graphql';

let httpLink;

// httpLink = new HttpLink({ uri: `https://staging.lynks.com/graphql` });
httpLink = new HttpLink({ uri: 'https://api.lynks.com/graphql' });

if (process.env.NODE_ENV === 'production') {
  httpLink = new HttpLink({ uri: 'https://api.lynks.com/graphql' });
}

if (process.env.IS_SANDBOX === 'true') {
  httpLink = new HttpLink({ uri: 'https://sandbox-api.lynks.com/graphql' });
}

if (process.env.IS_STAGING === 'true') {
  httpLink = new HttpLink({ uri: 'https://staging-api.lynks.com/graphql' });
}

// Kept last on purpose so the manual switch wins over every branch above — but
// only where it is allowed to exist at all.
//
// The NODE_ENV test is FIRST for a reason: DefinePlugin rewrites it to
// `'production' !== 'production'`, so a production build sees `if (false && ...)`
// and UglifyJS removes the block outright — the localhost URL never reaches the
// bundle, rather than merely losing an `if` at runtime. In development the
// substitution is `'development' !== 'production'` and the switch behaves
// exactly as it always has.
if (process.env.NODE_ENV !== 'production' && USE_LOCAL_BACKEND) {
  httpLink = new HttpLink({ uri: LOCAL_GRAPHQL_URI });
}

// auth link
const middlewareAuthLink = new ApolloLink((operation, forward) => {
  const token =
    localStorage.getItem(AUTH_TOKEN) || localStorage.getItem(GUEST_TOKEN);
  const authorizationHeader = token ? `Bearer ${token}` : null;

  operation.setContext({
    headers: {
      authorization: authorizationHeader,
      'App-Locale': localStorage.getItem(LOCALE) || 'ar',
      'App-Session': JSON.stringify(sessionStorage),
    },
  });
  return forward(operation);
});

const httpLinkWithAuthToken = middlewareAuthLink.concat(httpLink);

// retry link
let retryToast;
let delay = 5000;
const retry = new RetryLink({
  // eslint-disable-next-line no-unused-vars
  delay: (count, operation, error) => {
    if (count > 1 && !(count % 5) && navigator.onLine) {
      delay += 5000;
      if (!toast.isActive(retryToast)) {
        retryToast = toast.warn(
          `Operation failed, will retry in ${delay / 1000} seconds.`,
          {
            autoClose: delay,
            onClose: () => {
              retryToast = null;
            },
          }
        );
      }
      return delay;
    }
    return 1000;
  },
  // eslint-disable-next-line no-unused-vars
  attempts: (count, operation, error) => {
    if (!error) {
      retryToast = null;
      return false;
    }
    if (error.statusCode === 401) {
      localStorage.removeItem(AUTH_TOKEN);
      localStorage.removeItem(GUEST_TOKEN);
      window.location.reload();
      return false;
    }
    if (error.statusCode === 500 && process.env.NODE_ENV !== 'production') {
      return false;
    }
    return true;
  },
});
const httpLinkWithAuthTokenAndRetry = retry.concat(httpLinkWithAuthToken);

// onError link
// eslint-disable-next-line no-unused-vars
let networkErrorToast;
const errorAfterware = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    console.log('graphQLErrors', graphQLErrors);
    // graphQLErrors.map(({ message, locations, path }) => {
    //   const msg = `[GraphQL error]: Message: ${message}, Location: {line: ${
    //     locations[0].line
    //   }, column:${locations[0].column}}, Path: ${path}`;
    //   // eslint-disable-next-line no-console
    //   console.log(msg);
    //   // Should be handled case by case
    //   // if (!toast.isActive(graphQLErrorToast)) {
    //   //   graphQLErrorToast = toast.error("Something went wrong, our team got notified about this issue, we're working hard to get our beta version stable as soon as possible.", {
    //   //     onClose: () => {graphQLErrorToast = null}
    //   //   })
    //   // }
    // });
  }

  if (networkError) {
    console.log('networkError', networkError);
    // const msg = `[Network error]: ${networkError}`;
    // logger.error(msg);
    // if (!toast.isActive(networkErrorToast)) {
    //   networkErrorToast = toast.warn(
    //     'We are having issues to connect with our servers, please try again later.',
    //     {
    //       autoClose: false,
    //       onClose: () => {
    //         networkErrorToast = null;
    //       },
    //     }
    //   );
    // }
    // // console.log(msg)
  }
});

const httpLinkWithAuthTokenAndRetryAndErrorHandling = errorAfterware.concat(
  httpLinkWithAuthTokenAndRetry
);

// const wsLink = new WebSocketLink({
//   uri: `ws://localhost:3030/graphql`,
//   options: {
//     reconnect: true,
//     connectionParams: {
//       authToken: token,
//     },
//   }
// })

// const link = split(
//   ({ query }) => {
//     const { kind, operation } = getMainDefinition(query)
//     return kind === 'OperationDefinition' && operation === 'subscription'
//   },
//   wsLink,
//   httpLinkWithAuthToken,
// )

const apolloLink = ApolloLink.from([stateLink, httpLinkWithAuthTokenAndRetry]);

export default apolloLink;



// WEBPACK FOOTER //
// ./app/apolloLink.js